import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";

import { LocalStore } from "@/utils/LocalStore";

import { useUserStore } from "./user";

const ONBOARDING_STEP_IDS = ["welcome-video", "guide"] as const;
export type OnboardingStepId = (typeof ONBOARDING_STEP_IDS)[number];

interface OnboardingState {
  eligible: boolean;
  completedSteps: Record<string, boolean>;
  hasOpenedGuide: boolean;
}

const defaultState: OnboardingState = {
  eligible: false,
  completedSteps: {},
  hasOpenedGuide: false,
};

export const useOnboardingStore = defineStore("onboarding", () => {
  const userId = useUserStore().technicalUser.id;
  const localStore = new LocalStore<OnboardingState>(`onboardingFlow:${userId}`);

  const state = ref<OnboardingState>(localStore.load() ?? { ...defaultState });

  let onMenuTooltip: (() => void) | null = null;

  function registerMenuTooltipHandler(handler: (() => void) | null) {
    onMenuTooltip = handler;
  }

  function triggerMenuTooltip() {
    onMenuTooltip?.();
  }

  watch(state, () => localStore.save(state.value), { deep: true });

  const currentStep = computed<OnboardingStepId | null>(() => {
    if (!state.value.eligible) return null;
    return ONBOARDING_STEP_IDS.find((id) => !state.value.completedSteps[id]) ?? null;
  });

  const isComplete = computed(() => state.value.eligible && currentStep.value === null);

  const shouldPulseMenuItem = computed(() => state.value.eligible && !state.value.hasOpenedGuide);

  function enroll() {
    state.value.eligible = true;
  }

  function completeStep(stepId: OnboardingStepId) {
    state.value.completedSteps[stepId] = true;
  }

  function markGuideOpened() {
    state.value.hasOpenedGuide = true;
  }

  function reset() {
    state.value = { ...defaultState, eligible: true };
  }

  return {
    currentStep,
    isComplete,
    shouldPulseMenuItem,
    registerMenuTooltipHandler,
    triggerMenuTooltip,
    enroll,
    completeStep,
    markGuideOpened,
    reset,
  };
});
